import { memo, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Box, Typography, Avatar } from '@mui/material';
import { useReactiveVar } from '@apollo/client';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import theme from 'src/style-system/theme';
import { usePersistentplanAndFreq } from 'store/planAndFreq';
import { mealCountWithoutSides } from 'store/productsFlatList';
import { isCartOpen } from 'store/cartItems';
import { ICountryList, plansType } from '@utils/types';
import CartBag from '@public/assets/svg/CartBag.svg';
import TCCLogo from '@components/TCCLogo';
import PlanAndPriceButtonGroup from '@components/PlanAndPriceButtonGroup';
import NewFlowFAQ from '@components/NewFlowFAQ';
import FrequencyButtonGroup from '@components/FrequencyButtonGroup';

import { useStyles } from './useStyles';

interface IPlan {
  plans: plansType[] | undefined;
  frequencies: string[] | undefined;
  isHeaderExpanded: boolean;
  defaultCountry: ICountryList | undefined;
  setCountryDrawerOpen: (open: boolean) => void;
}

const Plan = ({
  plans,
  defaultCountry,
  setCountryDrawerOpen,
  isHeaderExpanded,
  frequencies,
}: IPlan) => {
  const [open, setOpen] = useState<boolean>(false);
  const planAndFreq = usePersistentplanAndFreq();
  const mealCounts = useReactiveVar(mealCountWithoutSides);

  const router = useRouter();

  const { TRAIL_PLAN } = router?.query;

  const getClasses = useStyles(open, isHeaderExpanded);
  const classes = getClasses();

  const orderFlowPath = localStorage.getItem('ab_test')
    ? JSON.parse(localStorage.getItem('ab_test') || '')
    : '';

  const handleRouteToHomePage = () => {
    router.push('/');
  };

  // on scroll close the options menu

  const handleScroll = (open: boolean) => {
    open && setOpen(false);
  };

  useEffect(() => {
    window.addEventListener('scroll', () => handleScroll(open));

    return () => {
      window.removeEventListener('scroll', () => handleScroll(open));
    };
  }, [open]);

  return (
    <Box
      bgcolor={open ? '#FFF2E8' : 'offWhite.main'}
      className={classes.mainContainer}
    >
      <Box className={classes.countryAvatarWrapper}>
        <Box className={classes.logoAvatarWrapper}>
          <Box
            sx={{
              cursor: 'pointer',
              [theme.breakpoints.down('md')]: {
                transform: 'scale(0.8)',
              },
            }}
            onClick={handleRouteToHomePage}
          >
            <TCCLogo />
          </Box>
          {TRAIL_PLAN ? null : (
            <Avatar
              src={defaultCountry?.country_flag}
              alt={defaultCountry?.iso_alpha_2_code || 'US'}
              onClick={() => setCountryDrawerOpen(true)}
              sx={{
                cursor: 'pointer',
                width: { xs: '30px', md: '40px' },
                height: { xs: '30px', md: '40px' },
              }}
            />
          )}
        </Box>
      </Box>

      <Typography
        variant="h2"
        color="cinnamon.main"
        align="center"
        sx={{
          flex: 2,
          display: isHeaderExpanded && !TRAIL_PLAN ? 'none' : 'block',
        }}
      >
        {TRAIL_PLAN ? 'Pick 2 trial meals' : 'Build Your Box'}
      </Typography>

      {/* Plan and freq dropdown show on scroll */}
      {TRAIL_PLAN ? null : (
        <Box
          className={classes.selectBox}
          sx={{
            display: isHeaderExpanded ? 'flex' : 'none',
            cursor: 'pointer',
          }}
          onClick={() => setOpen((prev) => !prev)}
        >
          <Typography variant="body2" color="garamMasala.main" align="center">
            {planAndFreq.selectedPlan} Meals
          </Typography>
          {open ? (
            <KeyboardArrowUpIcon
              fontSize="medium"
              sx={{ color: 'garamMasalamain', padding: 0 }}
            />
          ) : (
            <KeyboardArrowDownIcon
              fontSize="medium"
              sx={{ color: 'garamMasalamain', padding: 0 }}
            />
          )}
        </Box>
      )}
      <Box className={classes.faqAndCartWrapper}>
        <NewFlowFAQ pageName="step_2" />
        <Box
          className={classes.cartBagWrapper}
          onClick={() => isCartOpen(true)}
        >
          <CartBag />
          <Typography variant="body2" color="whitePepper.main">
            {mealCounts}/{planAndFreq.selectedPlan}
          </Typography>
        </Box>
      </Box>

      {/* Button option below 1020 px */}
      {TRAIL_PLAN ? null : (
        <Box
          bgcolor="#FFF2E8"
          className={classes.planBtnContainer}
          sx={{ display: open ? 'flex' : 'none' }}
          onClick={() => setOpen(false)}
        >
          <Box className={classes.planBtnWrapper}>
            <Typography variant="body1" color="garamMasala.main" align="left">
              Plan Size
            </Typography>
            <Typography
              variant="caption"
              color="cinnamon.main"
              align="left"
              mb="12px"
            >
              Each meal serves 1 adult
            </Typography>
            <PlanAndPriceButtonGroup plans={plans} />
            <Typography variant="body1" color="garamMasala.main" mt="15px">
              Frequency
            </Typography>
            <Typography
              variant="caption"
              color="cinnamon.main"
              align="left"
              mb="12px"
            >
              Pause/cancel anytime, no questions asked
            </Typography>
            <FrequencyButtonGroup frequencies={frequencies} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default memo(Plan);
